import { Search, X } from "lucide-react"
import React, { useEffect, useRef, useState } from "react"
import ReactDOM from 'react-dom/client'
import PackagingGuide from "./components/packaging-guide"
import ProcessingGuide from "./components/processing-guide"
import { ThemeProvider } from "./components/theme-provider"
import { Button } from "./components/ui/button"
import { Input } from "./components/ui/input"
import { themeConfig } from "./lib/theme-config"
import type { ProcessingData } from "./lib/types"
import { tailwindStyles } from './styles/tailwind-inline'

// Utility functions for URL query parameters
const getUrlParams = () => {
    try {
        const urlParams = new URLSearchParams(window.location.search)
        const search = urlParams.get('search') || ''
        const tab = (urlParams.get('tab') as 'processing' | 'packaging') || 'processing'
        const packagingType = urlParams.get('packagingType') || ''
        return { search, tab, packagingType }
    } catch (error) {
        console.error('Error reading URL params:', error)
        return { search: '', tab: 'processing' as const, packagingType: '' }
    }
}

const updateUrlParams = (search: string, tab: 'processing' | 'packaging', packagingType?: string) => {
    const url = new URL(window.location.href)
    if (search) {
        url.searchParams.set('search', search)
    } else {
        url.searchParams.delete('search')
    }
    url.searchParams.set('tab', tab)
    
    if (packagingType) {
        url.searchParams.set('packagingType', packagingType)
    } else {
        url.searchParams.delete('packagingType')
    }
    
    // Update URL without triggering a page reload
    window.history.replaceState({}, '', url.toString())
}

export function ProcessingIcon({ color = "#0A4D6E" }: { color?: string }) {
    return (
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask
                id="mask0_2094_777"
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="36"
                height="36"
                {...{ masktype: "alpha" } as any}
            >
                <rect width="36" height="36" fill="#D9D9D9" />
            </mask>
            <g mask="url(#mask0_2094_777)">
                <path d="M22.5 31.5V27H16.5V12H13.5V16.5H3V4.5H13.5V9H22.5V4.5H33V16.5H22.5V12H19.5V24H22.5V19.5H33V31.5H22.5ZM25.5 13.5H30V7.5H25.5V13.5ZM25.5 28.5H30V22.5H25.5V28.5ZM6 13.5H10.5V7.5H6V13.5Z" fill={color} />
            </g>
        </svg>
    )
}

export function PackagingIcon({ color = "#0A4D6E" }: { color?: string }) {
    return (
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask
                id="mask0_2094_782"
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="36"
                height="36"
                {...{ masktype: "alpha" } as any}
            >
                <rect width="36" height="36" fill="#D9D9D9" />
            </mask>
            <g mask="url(#mask0_2094_782)">
                <path d="M16.5 29.1375V18.8625L7.5 13.65V23.925L16.5 29.1375ZM19.5 29.1375L28.5 23.925V13.65L19.5 18.8625V29.1375ZM18 16.275L26.8875 11.1375L18 6L9.1125 11.1375L18 16.275ZM6 26.55C5.525 26.275 5.15625 25.9125 4.89375 25.4625C4.63125 25.0125 4.5 24.5125 4.5 23.9625V12.0375C4.5 11.4875 4.63125 10.9875 4.89375 10.5375C5.15625 10.0875 5.525 9.725 6 9.45L16.5 3.4125C16.975 3.1375 17.475 3 18 3C18.525 3 19.025 3.1375 19.5 3.4125L30 9.45C30.475 9.725 30.8438 10.0875 31.1063 10.5375C31.3688 10.9875 31.5 11.4875 31.5 12.0375V23.9625C31.5 24.5125 31.3688 25.0125 31.1063 25.4625C30.8438 25.9125 30.475 26.275 30 26.55L19.5 32.5875C19.025 32.8625 18.525 33 18 33C17.475 33 16.975 32.8625 16.5 32.5875L6 26.55Z" fill={color} />
            </g>
        </svg>

    )
}

interface PackageType {
    package_type: string
    id: string
    package_category: string
}

interface StandaloneComponentProps {
    // Props that can be passed from the CMS
    initialQuery?: string
    apiEndpoint?: string
    theme?: 'light' | 'dark' | 'system'
    containerClassName?: string
    onSearch?: (query: string) => void
    onGuideGenerated?: (data: ProcessingData) => void
}

export function StandaloneComponent({
    initialQuery = '',
    apiEndpoint = 'https://your-api.com',
    theme = 'light',
    containerClassName = '',
    onSearch,
    onGuideGenerated,
}: StandaloneComponentProps) {
    // Add refs for Shadow DOM
    const containerRef = useRef<HTMLDivElement>(null)
    const shadowRootRef = useRef<ShadowRoot | null>(null)
    const shadowContainerRef = useRef<HTMLDivElement | null>(null)

    // Create Shadow DOM after initial state is loaded
    useEffect(() => {        
        if (containerRef.current && !shadowRootRef.current) {
            // Create shadow root
            const shadowRoot = containerRef.current.attachShadow({ mode: 'open' })
            shadowRootRef.current = shadowRoot

            // Create container inside shadow DOM
            const shadowContainer = document.createElement('div')
            shadowContainer.id = 'shadow-container'
            shadowContainer.className = `manufacturing-guide-widget ${containerClassName}`
            shadowRoot.appendChild(shadowContainer)
            shadowContainerRef.current = shadowContainer

            // Inject Tailwind CSS immediately as a string
            const styleElement = document.createElement('style')
            styleElement.textContent = tailwindStyles || ''
            shadowRoot.prepend(styleElement)

            // Render React component into shadow DOM
            const root = ReactDOM.createRoot(shadowContainer)
            root.render(<ComponentContent />)
        }
    }, []) // Remove all dependencies

    // Extract the main component content
    const ComponentContent = () => {
        // Initialize state from URL parameters or props
        const [searchQuery, setSearchQuery] = useState('')
        const [activeTab, setActiveTab] = useState<"processing" | "packaging">("processing")
        const [suggestedTerms, setSuggestedTerms] = useState<string[]>([])
        const [isLoading, setIsLoading] = useState(true)
        const [hasSearched, setHasSearched] = useState(false)
        const [packagingTypes, setPackagingTypes] = useState<PackageType[]>([])
        const [allPackageTypes, setAllPackageTypes] = useState<PackageType[]>([])
        const [selectedPackagingType, setSelectedPackagingType] = useState<string>('')
        const [isInitialized, setIsInitialized] = useState(false)
        const [searchQueryFromUrl, setSearchQueryFromUrl] = useState(false)

        // Initialize state from URL parameters on component mount
        useEffect(() => {
            const urlParams = getUrlParams()
            const hasUrlSearch = urlParams.search || initialQuery
            setSearchQuery(hasUrlSearch)
            setActiveTab(urlParams.tab)
            setSelectedPackagingType(urlParams.packagingType)
            setSearchQueryFromUrl(!!hasUrlSearch)
            setIsInitialized(true)
        }, [])

        // Update URL when search, tab, or packaging type changes
        useEffect(() => {
            updateUrlParams(searchQuery, activeTab, selectedPackagingType)
        }, [searchQuery, activeTab, selectedPackagingType])

        useEffect(() => {
            const loadSuggestions = async () => {
                try {
                    // Mock suggested terms - in real app, this would be an API call
                    const terms = [
                        "Chocolate Chip Cookies",
                        "Frozen Peas",
                        "Beef Jerky",
                        "Candles",
                        "Soda",
                        "Wine",
                        "Toothpaste",
                        "Lipstick",
                        "Soap",
                        "Tea",
                        "Coffee",
                    ]
                    setSuggestedTerms(terms)
                } catch (error) {
                    console.error("Failed to load suggested search terms:", error)
                    setSuggestedTerms([])
                } finally {
                    setIsLoading(false)
                }
            }

            loadSuggestions()
        }, [])

        const performSearch = async (query: string) => {
            setHasSearched(true)

            try {
                // Call the onSearch callback if provided
                if (onSearch) {
                    onSearch(query.trim())
                }

                // Make concurrent API calls
                const [classifierResponse, packageTypesResponse] = await Promise.all([
                    fetch(`${apiEndpoint}/api/package-type-classifier`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ query: query.trim() })
                    }),
                    fetch(`${apiEndpoint}/api/package-types`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                        }
                    })
                ])

                if (!classifierResponse.ok) {
                    throw new Error('Package type classifier API call failed')
                }

                if (!packageTypesResponse.ok) {
                    throw new Error('Package types API call failed')
                }

                const [packagingTypesData, allPackageTypesData] = await Promise.all([
                    classifierResponse.json(),
                    packageTypesResponse.json()
                ])

                setPackagingTypes(packagingTypesData.slice(0, 4))
                setAllPackageTypes(allPackageTypesData)

                const guideData: ProcessingData = {
                    name: query.trim(),
                    packagingTypes: packagingTypesData.slice(0, 4),
                }

                // Call the onGuideGenerated callback if provided
                if (onGuideGenerated) {
                    onGuideGenerated(guideData)
                }

                // You could also redirect to a guide page or show results inline
                console.log('Guide generated:', guideData)

            } catch (error) {
                console.error("Error generating guide:", error)
                // Handle error - maybe show a toast or error message
            }
        }

        const handleSearch = async (e: React.FormEvent) => {
            e.preventDefault()
            if (!searchQuery.trim()) return
            await performSearch(searchQuery)
        }

        const handleSearchFromUrl = async (query: string) => {
            if (!query.trim()) return
            await performSearch(query)
        }

        // Auto-trigger search if URL has search parameter on page load
        // This effect runs after the component is initialized
        useEffect(() => {
            if (isInitialized && searchQuery && !hasSearched && searchQueryFromUrl) {
                handleSearchFromUrl(searchQuery)
            }
        }, [isInitialized, searchQuery, hasSearched, searchQueryFromUrl])

        // Auto-trigger packaging type selection if URL has packaging type parameter
        // This effect runs after the component is initialized and data is loaded
        useEffect(() => {
            if (isInitialized && selectedPackagingType && packagingTypes.length > 0 && !hasSearched) {
                // Find the packaging type in the loaded data
                const foundType = packagingTypes.find(type => type.package_type === selectedPackagingType)
                if (foundType) {
                    // Trigger the packaging type selection
                    handlePackagingTypeSelect(selectedPackagingType)
                }
            }
        }, [isInitialized, selectedPackagingType, packagingTypes, hasSearched])

        const handleSuggestionClick = async (suggestion: string) => {
            setSearchQuery(suggestion)
            setSearchQueryFromUrl(false) // Reset flag since this is user interaction
            // Auto-submit when suggestion is clicked
            await performSearch(suggestion)
        }

        const handleTabChange = (tab: "processing" | "packaging") => {
            setActiveTab(tab)
        }

        const handlePackagingTypeSelect = (packagingType: string) => {
            setSelectedPackagingType(packagingType)
        }

        const handleClear = () => {
            setSearchQuery('')
            setHasSearched(false)
            setPackagingTypes([])
            setSelectedPackagingType('')
            setActiveTab('processing')
            setSearchQueryFromUrl(false)
            // Clear URL parameters
            updateUrlParams('', 'processing', '')
        }

        return (
            <ThemeProvider 
                attribute="class" 
                defaultTheme={theme} 
                enableSystem 
                disableTransitionOnChange
            >
                <div className="flex flex-col items-center justify-center max-w-3xl mx-auto p-4">
                    {/*<h1 className="text-3xl font-bold mb-4" style={{ color: themeConfig.colors.primary }}>
                        AI Search Pilot
                    </h1>
                    <p className="text-center text-gray-600 mb-10">
                        Enter your end product, choose a Processing or Packaging guide, and get tailored equipment recommendations (AI responses are experimental).
                    </p>*/}

                    <form onSubmit={handleSearch} className="flex flex-col sm:flex-row w-full mb-10 gap-4 sm:gap-0">
                        <div className="relative flex-grow">
                            <Input
                                type="text"
                                placeholder="Type what you want to manufacture"
                                className="pr-20 h-12"
                                style={{ 
                                    borderColor: themeConfig.colors.primary,
                                    backgroundColor: '#F0F5F2',
                                    color: themeConfig.colors.primary,
                                    fontWeight: 500,
                                    fontStyle: 'italic',
                                }}
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value)
                                    setSearchQueryFromUrl(false) // Reset flag when user types
                                }}
                            />
                            {searchQuery && (
                                <button 
                                    type="button" 
                                    onClick={handleClear}
                                    className="absolute right-10 top-1/2 -translate-y-1/2 hover:bg-gray-200 rounded-full p-1 transition-colors"
                                >
                                    <X className="h-4 w-4 text-gray-500" />
                                </button>
                            )}
                            <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2">
                                <Search className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
                        <Button 
                            type="submit" 
                            className="sm:ml-4 h-12 text-white text-base w-full sm:w-auto" 
                            style={{ backgroundColor: themeConfig.colors.primary }}
                        >
                            GENERATE GUIDE
                        </Button>
                    </form>

                    <div className="w-full max-w-xl">
                        {isLoading && !hasSearched && (
                            <p className="text-gray-700 mb-4">Loading suggestions...</p>
                        )}
                        {!isLoading && !hasSearched && (
                            <>
                                <p className="text-gray-700 mb-4">Or try one of these:</p>
                                <div className="flex flex-wrap gap-2">
                                    {suggestedTerms.map((item) => (
                                        <button
                                            key={item}
                                            onClick={() => handleSuggestionClick(item)}
                                            className="px-4 py-2 rounded-md hover:bg-opacity-80 transition-colors"
                                            style={{
                                                backgroundColor: `${themeConfig.colors.primary}10`,
                                                color: themeConfig.colors.primary,
                                            }}
                                        >
                                            {item}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {hasSearched && (
                        <div className="w-full max-w-3xl">
                            <h2 className="text-xl font-bold mb-4 text-center" style={{ color: themeConfig.colors.primary }}>
                                Which guide would you like to generate?
                            </h2>

                            <div className="flex justify-center gap-6 mb-6">
                                <button
                                    className={`flex flex-col items-center justify-center rounded-xl shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary font-bold text-lg select-none ${activeTab === "processing" ? "bg-[#0A4D6E] text-white" : "bg-[#F5F8F7] text-[#0A4D6E]"}`}
                                    style={{
                                        width: 184,
                                        height: 112,
                                    }}
                                    onClick={() => handleTabChange("processing")}
                                >
                                    <span className="mb-4">
                                        <ProcessingIcon color={activeTab === "processing" ? "white" : "#0A4D6E"} />
                                    </span>
                                    PROCESSING
                                </button>
                                <button
                                    className={`flex flex-col items-center justify-center rounded-xl shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary font-bold text-lg select-none ${activeTab === "packaging" ? "bg-[#0A4D6E] text-white" : "bg-[#F5F8F7] text-[#0A4D6E]"}`}
                                    style={{
                                        width: 184,
                                        height: 112,
                                    }}
                                    onClick={() => handleTabChange("packaging")}
                                >
                                    <span className="mb-4">
                                        <PackagingIcon color={activeTab === "packaging" ? "white" : "#0A4D6E"} />
                                    </span>
                                    PACKAGING
                                </button>
                            </div>

                            {activeTab === "processing" && (
                                <ProcessingGuide productName={searchQuery} />
                            )}
                            {activeTab === "packaging" && (                                
                                <PackagingGuide 
                                    productName={searchQuery} 
                                    packagingTypes={packagingTypes} 
                                    allPackageTypes={allPackageTypes}
                                    selectedPackagingType={selectedPackagingType}
                                    onPackagingTypeSelect={handlePackagingTypeSelect}
                                />
                            )}
                        </div>
                    )}
                </div>
            </ThemeProvider>
        )
    }

    // Return the container div that will host the shadow DOM
    return <div ref={containerRef} className={containerClassName} />
} 